/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {combineEpics, Epic} from 'redux-observable';
import {filter, map, mapTo, mergeMap} from 'rxjs/operators';
import {isActionOf} from 'typesafe-actions';
import {accountEmailChangeResource, iamStompClient} from '../services';
import {ACCOUNT_EMAIL_CHANGE_STATE_TOPIC_PREFIX} from '../services/Stomp/topics';
import {
	accountEmailChangeSceneActivated,
	accountEmailChangeSceneDeactivated,
	accountEmailChangeSceneRetrieveStateNotFound,
	accountEmailChangeSceneRetrieveStateUnexpectedError,
	accountEmailChangeSceneStateChanged,
	accountEmailChangeSceneStateRetrieved
} from '../scenes/AccountEmailChange/actions';
import {stompClientSubscribedToTopic, stompClientUnsubscribedFromTopic, stompMessageReceived} from '../services/Stomp/actions';
import {
	iamApiAccountEmailChangeStateRetrieved,
	iamApiRetrieveAccountEmailChangeStateNotFound,
	iamApiRetrieveAccountEmailChangeStateUnexpectedError
} from '../services/IamApi/actions';
import {STATE_CHANGE_FOR_ACCOUNT_EMAIL_CHANGE} from '../services/Stomp/EventType';
import {AccountEmailChangeStateDTO} from '../generated/iam-client';

const attemptIdFromQueryString: () => string = () => new URLSearchParams(window.document.location.search).get('attemptId') || '';

const subscribeToAccountEmailChangeStateTopicOnSceneActivated: Epic = (action$) => action$.pipe(
	filter(isActionOf(accountEmailChangeSceneActivated)),
	mergeMap(() => iamStompClient.subscribe(ACCOUNT_EMAIL_CHANGE_STATE_TOPIC_PREFIX + attemptIdFromQueryString()))
);

// noinspection DuplicatedCode
const unsubscribeFromAccountEmailChangeStateTopicOnSceneDeactivated: Epic = (action$) => {
	let subscribedTopic: string | null = null;

	action$.pipe(
		filter(isActionOf(stompClientSubscribedToTopic)),
		filter((action) => action.payload.topic.startsWith(ACCOUNT_EMAIL_CHANGE_STATE_TOPIC_PREFIX))
	).subscribe((action) => subscribedTopic = action.payload.topic);

	action$.pipe(
		filter(isActionOf(stompClientUnsubscribedFromTopic)),
		filter((action) => action.payload.topic === subscribedTopic)
	).subscribe(() => subscribedTopic = null);

	return action$.pipe(
		filter(isActionOf(accountEmailChangeSceneDeactivated)),
		filter(() => subscribedTopic !== null),
		mergeMap(() => iamStompClient.unsubscribe(String(subscribedTopic)))
	);
};

const retrieveAccountEmailChangeStateOnSubscribedToTopic: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompClientSubscribedToTopic)),
	filter((action) => action.payload.topic.startsWith(ACCOUNT_EMAIL_CHANGE_STATE_TOPIC_PREFIX)),
	mergeMap(() => accountEmailChangeResource.accountEmailChangeState(attemptIdFromQueryString()))
);

const notifySceneOfAccountEmailChangeStateRetrieved: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAccountEmailChangeStateRetrieved)),
	map((action) => accountEmailChangeSceneStateRetrieved(action.payload))
);

const notifySceneOfRetrieveAccountEmailChangeStateNotFound: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiRetrieveAccountEmailChangeStateNotFound)),
	mapTo(accountEmailChangeSceneRetrieveStateNotFound())
);

const notifySceneOfRetrieveAccountEmailChangeStateUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiRetrieveAccountEmailChangeStateUnexpectedError)),
	mapTo(accountEmailChangeSceneRetrieveStateUnexpectedError())
);

const notifySceneOfStateChangeForAccountEmailChange: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === STATE_CHANGE_FOR_ACCOUNT_EMAIL_CHANGE),
	map((action) => accountEmailChangeSceneStateChanged({state: action.payload.eventBody as unknown as AccountEmailChangeStateDTO}))
);

const accountEmailChangeEpics: Epic = combineEpics(
	subscribeToAccountEmailChangeStateTopicOnSceneActivated,
	unsubscribeFromAccountEmailChangeStateTopicOnSceneDeactivated,
	retrieveAccountEmailChangeStateOnSubscribedToTopic,
	notifySceneOfAccountEmailChangeStateRetrieved,
	notifySceneOfRetrieveAccountEmailChangeStateNotFound,
	notifySceneOfRetrieveAccountEmailChangeStateUnexpectedError,
	notifySceneOfStateChangeForAccountEmailChange
);

export default accountEmailChangeEpics;
